import { ApplicationConfig, inject, LOCALE_ID, provideAppInitializer, provideBrowserGlobalErrorListeners, provideZonelessChangeDetection, isDevMode } from '@angular/core';
import { provideRouter, withViewTransitions } from '@angular/router';

import { routes } from './app.routes';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { provideTranslateService, TranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { firstValueFrom } from 'rxjs';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { permissionErrorInterceptor } from './core/interceptors/permission.interceptor';
import { SessionService } from './core/auth/services/session/session.service';

import { registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import localeEn from '@angular/common/locales/en';
import { provideServiceWorker } from '@angular/service-worker';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';

registerLocaleData(localeEs);
registerLocaleData(localeEn);

export const appConfig: ApplicationConfig = {
  providers: [
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideRouter(routes, withViewTransitions()),
    provideHttpClient(withFetch(), withInterceptors([authInterceptor, permissionErrorInterceptor])),
    provideTranslateService({
      fallbackLang: 'en',
      loader: provideTranslateHttpLoader({
        prefix: '/i18n/',
        suffix: '.json',
      }),
    }),
    provideAppInitializer(async () => {
      const translate = inject(TranslateService);
      // Priorizar el idioma guardado en localStorage, si existe
      const savedLang = localStorage.getItem('app_lang');
      const browserLang = translate.getBrowserLang() || 'en';
      const lang = savedLang || browserLang;
      try {
        await firstValueFrom(translate.use(lang));
      } catch {
        // Si falla la carga del idioma detectado, intentar con el fallback
        try {
          await firstValueFrom(translate.use('en'));
        } catch {
          // Continuar sin traducciones si todo falla (se mostrarán las claves)
        }
      }
    }),
    // Vigía de sesión: renovación silenciosa + aviso antes de caducar
    provideAppInitializer(() => {
      inject(SessionService).start();
    }),
    { provide: LOCALE_ID, useValue: 'es-ES' },
    // SW custom (sw.js) que extiende ngsw-worker.js con soporte de push notifications
    provideServiceWorker('sw.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
    provideCharts(withDefaultRegisterables()),
  ],
};
